import React, { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Button,
  Form,
  Table,
  Pagination,
  Spinner,
  Alert,
  Modal
} from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { useAuth0 } from '@auth0/auth0-react';
import { formatCalendarDate } from '../../utils/dateFormat';
import { BIN_DEFINITIONS, LOCATIONS } from './binDefinitions';

export const route = '/maintenance/daily-service-requests';
export const role = 'Maintenance';

const DailyTrashServiceRequests = () => {
  const { user, isAuthenticated } = useAuth0();

  // raw data
  const [rawRequests, setRawRequests] = useState([]);

  // pagination
  const [page, setPage] = useState(1);
  const limit = 10;
  const [totalPages, setTotalPages] = useState(1);

  // loading & error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterBin, setFilterBin] = useState('');

  // new-request form
  const [showForm, setShowForm] = useState(false);
  const [serviceDate, setServiceDate] = useState('');
  const [pickups, setPickups] = useState(
    BIN_DEFINITIONS.map(b => ({ ...b, picked: false }))
  );

  // detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // fetch rawRequests on mount and when date range changes
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: 1,
          limit: 1000,
          ...(startDate && { startDate }),
          ...(endDate && { endDate })
        });
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/daily-trash-service-requests?${params}`
        );
        const data = await res.json();
        setRawRequests(data.requests);
        setPage(1);
      } catch {
        setError('Failed to load data.');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [startDate, endDate]);

  // apply location & bin filters (no pagination)
  const filteredRequests = useMemo(() => {
    return rawRequests.filter(r => {
      if (filterLocation) {
        const matchLoc = r.pickups.some(p =>
          p.picked &&
          BIN_DEFINITIONS.find(b => b.code === p.code)?.group === filterLocation
        );
        if (!matchLoc) return false;
      }
      if (filterBin) {
        const matchBin = r.pickups.some(p => p.picked && p.code === filterBin);
        if (!matchBin) return false;
      }
      return true;
    });
  }, [rawRequests, filterLocation, filterBin]);

  // paginate filteredRequests
  const paginatedRequests = useMemo(() => {
    const pages = Math.max(1, Math.ceil(filteredRequests.length / limit));
    setTotalPages(pages);
    const pg = Math.min(page, pages);
    setPage(pg);
    return filteredRequests.slice((pg - 1) * limit, pg * limit);
  }, [filteredRequests, page]);

  // === UPDATED EXPORT: add header and respect filters ===
  const exportToExcel = () => {
    // tally per bin code
    const tally = {};
    filteredRequests.forEach(req => {
      req.pickups.forEach(p => {
        if (p.picked) {
          tally[p.code] = (tally[p.code] || 0) + 1;
        }
      });
    });

    // determine which locations to show
    const locationsToShow = filterLocation
      ? [filterLocation]
      : LOCATIONS;

    // build header rows
    const header = [];
    if (startDate || endDate) {
      header.push([
        'Date Range:',
        `${startDate || '—'} to ${endDate || '—'}`
      ]);
    }
    if (filterLocation) {
      header.push(['Location:', filterLocation]);
    }
    if (filterBin) {
      header.push(['Bin:', filterBin]);
    }
    header.push([]);

    // build AOAs
    const aoa = [...header];
    locationsToShow.forEach(loc => {
      aoa.push([loc]);                     // pier header
      aoa.push(['Bin', 'Times Serviced']); // column headers
      BIN_DEFINITIONS
        .filter(b => b.group === loc)
        .filter(b => !filterBin || b.code === filterBin)
        .forEach(bin => {
          aoa.push([
            `${bin.code} — ${bin.label}`,
            tally[bin.code] || 0
          ]);
        });
      aoa.push([]); // blank line between groups
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const range = XLSX.utils.decode_range(ws['!ref']);
    // style headers
    for (let R = range.s.r; R <= range.e.r; ++R) {
      const cellA = XLSX.utils.encode_cell({ r: R, c: 0 });
      const val = ws[cellA]?.v;
      if (typeof val === 'string') {
        if (val === 'Date Range:' || val === 'Location:' || val === 'Bin:') {
          ws[cellA].s = { font: { bold: true, sz: 12 } };
        } else if (LOCATIONS.includes(val)) {
          ws[cellA].s = { font: { bold: true, sz: 14 } };
        } else if (val === 'Bin') {
          ws[cellA].s = { font: { bold: true } };
          const cellB = XLSX.utils.encode_cell({ r: R, c: 1 });
          if (ws[cellB]) ws[cellB].s = { font: { bold: true } };
        }
      }
    }
    ws['!cols'] = [{ wch: 35 }, { wch: 20 }];

    let filename = 'TrashServiceSummary';
    if (startDate || endDate) {
      const sd = startDate || '';
      const ed = endDate || '';
      filename += `_${sd}-${ed}`;
    }
    if (filterLocation) {
      // replace spaces/commas with underscores
      const loc = filterLocation.replace(/[^a-z0-9]/gi, '_');
      filename += `_${loc}`;
    }
    if (filterBin) {
      filename += `_${filterBin}`;
    }
    filename += '.xlsx';

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Summary');
    XLSX.writeFile(wb, filename);
  };

  // form helpers
  const togglePickup = idx =>
    setPickups(ps => ps.map((p, i) => i === idx ? { ...p, picked: !p.picked } : p));
  const handleCancel = () => {
    setShowForm(false);
    setServiceDate('');
    setPickups(BIN_DEFINITIONS.map(b => ({ ...b, picked: false })));
  };
  const handleView = r => {
    setSelectedRequest(r);
    setShowDetailModal(true);
  };

  // submit new request (includes CC’ing via Auth0 user.email)
  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/daily-trash-service-requests`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            serviceDate,
            pickups,
            userEmail: isAuthenticated ? user.email : undefined
          })
        }
      );
      handleCancel();
      setStartDate(serviceDate);
      setEndDate(serviceDate);
    } catch {
      setError('Submission failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="my-5">
      <h2>Daily Trash Service Requests</h2>

      {/* Filters */}
      <Form className="mb-3">
        <div className="d-flex flex-wrap gap-3 align-items-end">
          <Form.Group controlId="startDate">
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </Form.Group>
          <Form.Group controlId="endDate">
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
            />
          </Form.Group>
          <Form.Group controlId="filterLocation">
            <Form.Label>Location</Form.Label>
            <Form.Select
              value={filterLocation}
              onChange={e => { setFilterLocation(e.target.value); setPage(1); }}
            >
              <option value="">All</option>
              {LOCATIONS.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="filterBin">
            <Form.Label>Bin</Form.Label>
            <Form.Select
              value={filterBin}
              onChange={e => { setFilterBin(e.target.value); setPage(1); }}
            >
              <option value="">All</option>
              {BIN_DEFINITIONS.map(b => (
                <option key={b.code} value={b.code}>
                  {b.code} — {b.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
      </Form>

      {/* Export & New Request */}
      {!showForm && (
        <>
          <Button
            variant="success"
            onClick={exportToExcel}
            className="me-2 mb-3"
          >
            Export Summary to Excel
          </Button>
          <Button onClick={() => setShowForm(true)} className="mb-3">
            New Request
          </Button>
        </>
      )}
      {error && <Alert variant="danger">{error}</Alert>}

      {/* New Request Form */}
      {showForm && (
        <Form onSubmit={handleSubmit} className="mb-5">
          <Form.Group className="mb-3" controlId="serviceDate">
            <Form.Label>Service Date</Form.Label>
            <Form.Control
              type="date"
              value={serviceDate}
              onChange={e => setServiceDate(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Label>Picked Up</Form.Label>
          {Object.entries(
            pickups.reduce((acc, p, i) => {
              (acc[p.group] = acc[p.group] || []).push({ ...p, index: i });
              return acc;
            }, {})
          ).map(([grp, items]) => (
            <div key={grp} className="mb-4">
              <h5>{grp}</h5>
              {items.map(p => (
                <Form.Check
                  key={p.code}
                  type="checkbox"
                  id={p.code}
                  label={`${p.code} — ${p.label}`}
                  checked={p.picked}
                  onChange={() => togglePickup(p.index)}
                  className="mb-2"
                />
              ))}
            </div>
          ))}
          <div className="mt-3">
            <Button variant="secondary" onClick={handleCancel} className="me-2">
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading || !serviceDate}>
              {loading ? 'Submitting…' : 'Submit'}
            </Button>
          </div>
        </Form>
      )}

      {/* History List */}
      {loading && !showForm ? (
        <Spinner animation="border" className="d-block mx-auto my-5" />
      ) : (
        <>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Request Date</th>
                <th>Service Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRequests.map(r => {
                const reqDate = new Date(r.createdAt).toLocaleDateString();
                const svcDate = formatCalendarDate(r.serviceDate);
                return (
                  <tr key={r._id}>
                    <td>{reqDate}</td>
                    <td>{svcDate}</td>
                    <td>
                      <Button variant="info" size="sm" onClick={() => handleView(r)}>
                        View
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          {totalPages > 1 && (
            <Pagination className="justify-content-center">
              <Pagination.Prev
                onClick={() => setPage(p => Math.max(p - 1, 1))}
                disabled={page === 1}
              />
              {[...Array(totalPages)].map((_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={page === i + 1}
                  onClick={() => setPage(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => setPage(p => Math.min(p + 1, totalPages))}
                disabled={page === totalPages}
              />
            </Pagination>
          )}
        </>
      )}

      {/* Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Request: {selectedRequest && new Date(selectedRequest.createdAt).toLocaleDateString()} &nbsp;|&nbsp;
            Service: {selectedRequest && formatCalendarDate(selectedRequest.serviceDate)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && Object.entries(
            selectedRequest.pickups.reduce((acc, p) => {
              const loc = BIN_DEFINITIONS.find(b => b.code === p.code)?.group || p.group;
              (acc[loc] = acc[loc] || []).push(p);
              return acc;
            }, {})
          ).map(([loc, bins]) => (
            <div key={loc} className="mb-4">
              <h5>{loc}</h5>
              <Table size="sm">
                <thead><tr><th>Bin</th><th>Service Requested?</th></tr></thead>
                <tbody>
                  {bins.map(p => (
                    <tr key={p.code}>
                      <td>{p.code} — {p.label}</td>
                      <td>{p.picked ? '✅' : '❌'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default DailyTrashServiceRequests;